/**
 * StoryCreationProgress Component
 *
 * Displays progress indicator for the story creation wizard steps.
 */

import { pxToRem } from '@/shared/styles/themes'
import { Box, styled, Typography } from '@mui/material'

import { StoryCreationStep, STEP_LABELS, STEP_ORDER } from './types'

interface StoryCreationProgressProps {
  currentStep: StoryCreationStep
}

const STEPS: { key: StoryCreationStep; label: string }[] = STEP_ORDER.map((key) => ({
  key,
  label: STEP_LABELS[key],
}))

export const StoryCreationProgress: React.FC<StoryCreationProgressProps> = ({
  currentStep,
}) => {
  const currentIndex = STEPS.findIndex((step) => step.key === currentStep)

  return (
    <StyledProgressContainer>
      <StyledProgressTrack>
        {STEPS.map((step, index) => {
          const isActive = index <= currentIndex
          const isCurrent = index === currentIndex

          return (
            <StyledStepWrapper key={step.key}>
              {index > 0 && <StyledConnector $active={index <= currentIndex} />}
              <StyledStep>
                <StyledStepIndicator $active={isActive} $current={isCurrent}>
                  {index + 1}
                </StyledStepIndicator>
                <StyledLabel $active={isActive}>
                  {step.label}
                </StyledLabel>
              </StyledStep>
            </StyledStepWrapper>
          )
        })}
      </StyledProgressTrack>
    </StyledProgressContainer>
  )
}

const StyledProgressContainer = styled(Box)`
  width: 100%;
  max-width: ${pxToRem(600)};
  margin: 0 auto ${pxToRem(32)};
`

const StyledProgressTrack = styled(Box)`
  display: flex;
  align-items: flex-start;
  justify-content: center;
`

const StyledStepWrapper = styled(Box)`
  display: flex;
  align-items: flex-start;
`

const StyledStep = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: ${pxToRem(32)};

  ${(props) => props.theme.breakpoints.up('sm')} {
    width: ${pxToRem(36)};
  }
`

const StyledConnector = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$active',
})<{ $active: boolean }>`
  width: ${pxToRem(40)};
  height: ${pxToRem(3)};
  margin-top: ${pxToRem(15)};
  background-color: ${(props) =>
    props.$active ? props.theme.palette.primary.main : props.theme.palette.divider};
  transition: background-color 0.3s ease;

  ${(props) => props.theme.breakpoints.up('sm')} {
    width: ${pxToRem(60)};
    margin-top: ${pxToRem(17)};
  }

  ${(props) => props.theme.breakpoints.up('md')} {
    width: ${pxToRem(80)};
  }
`

const StyledStepIndicator = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$active' && prop !== '$current',
})<{ $active: boolean; $current: boolean }>`
  width: ${pxToRem(32)};
  height: ${pxToRem(32)};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: ${pxToRem(13)};
  background-color: ${(props) =>
    props.$active ? props.theme.palette.primary.main : props.theme.palette.divider};
  color: ${(props) =>
    props.$active ? props.theme.palette.common.white : props.theme.palette.text.secondary};
  box-shadow: ${(props) =>
    props.$current ? `0 0 0 3px ${props.theme.palette.primary.light}` : 'none'};
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;

  ${(props) => props.theme.breakpoints.up('sm')} {
    width: ${pxToRem(36)};
    height: ${pxToRem(36)};
    font-size: ${pxToRem(14)};
  }
`

const StyledLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== '$active',
})<{ $active: boolean }>`
  font-size: ${pxToRem(10)};
  font-weight: ${(props) => (props.$active ? 500 : 400)};
  color: ${(props) =>
    props.$active ? props.theme.palette.text.primary : props.theme.palette.text.secondary};
  text-align: center;
  white-space: nowrap;
  margin-top: ${pxToRem(8)};

  ${(props) => props.theme.breakpoints.up('sm')} {
    font-size: ${pxToRem(12)};
  }
`

export default StoryCreationProgress
